import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  Platform,
  Modal,
  Pressable,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { useTheme } from '../context/ThemeContext';
import HamburgerMenu from '../components/HamburgerMenu';
import ProfileIcon from '../components/ProfileIcon';
import Stopwatch from '../components/Stopwatch';
import { getApiUrl } from '../utils/apiConfig';
import { isFirstDegreeOrHigher } from '../utils/gradeAccess';

const WINDOWS = [
  { key: '7', label: 'Last 7 days' },
  { key: '30', label: 'Last 30 days' },
  { key: '90', label: 'Last 90 days' },
];

function heatEmoji(heat) {
  if (heat === 'red') return '🔴';
  if (heat === 'green') return '🟢';
  if (heat === 'yellow') return '🟡';
  return '⚪';
}

export default function ClubStatsPage({ navigation, route }) {
  const theme = useTheme();
  const stopwatchRef = useRef(null);
  const scrollRef = useRef(null);
  const detailYRef = useRef(0);
  const user = route?.params?.user;
  const userId = user?.id || route?.params?.userId;

  const [forbidden, setForbidden] = useState(false);
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [metric, setMetric] = useState('sessions');
  const [scopeStudentId, setScopeStudentId] = useState('all');
  const [students, setStudents] = useState([]);
  const [scopeOpen, setScopeOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const styles = useMemo(() => getStyles(theme), [theme]);

  const allowed = isFirstDegreeOrHigher(user);

  const loadStudents = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await fetch(
        `${getApiUrl()}/api/users?requester_id=${encodeURIComponent(String(userId))}`
      );
      if (!res.ok) return;
      const rows = await res.json();
      setStudents(Array.isArray(rows) ? rows : []);
    } catch {
      setStudents([]);
    }
  }, [userId]);

  const loadMatrix = useCallback(async () => {
    if (!userId || !allowed) return;
    setLoading(true);
    try {
      const q = new URLSearchParams({
        requesterId: String(userId),
        scopeStudentId: String(scopeStudentId),
        metric: metric === 'avg_per_student_week' ? 'avg_per_student_week' : 'sessions',
      });
      const res = await fetch(`${getApiUrl()}/api/club/course-activity-matrix?${q}`);
      if (res.status === 403) {
        setForbidden(true);
        setMatrix(null);
        return;
      }
      if (!res.ok) throw new Error('matrix');
      const data = await res.json();
      setMatrix(data);
      setForbidden(false);
    } catch {
      setMatrix(null);
    } finally {
      setLoading(false);
    }
  }, [userId, allowed, scopeStudentId, metric]);

  useEffect(() => {
    if (!allowed) {
      setForbidden(true);
      setLoading(false);
      return;
    }
    loadStudents();
  }, [allowed, loadStudents]);

  useEffect(() => {
    if (!allowed) return;
    loadMatrix();
  }, [allowed, loadMatrix]);

  const loadDetail = useCallback(
    async (courseId, days) => {
      if (!userId) return;
      setDetailLoading(true);
      setSelected({ courseId, days });
      try {
        const q = new URLSearchParams({
          requesterId: String(userId),
          courseId: String(courseId),
          days: String(days),
          scopeStudentId: String(scopeStudentId),
        });
        const res = await fetch(`${getApiUrl()}/api/club/course-activity-detail?${q}`);
        if (!res.ok) throw new Error('detail');
        const data = await res.json();
        setDetail(data);
        setTimeout(() => {
          scrollRef.current?.scrollTo?.({
            y: Math.max(0, detailYRef.current - 12),
            animated: true,
          });
        }, 100);
      } catch {
        setDetail(null);
      } finally {
        setDetailLoading(false);
      }
    },
    [userId, scopeStudentId]
  );

  if (!userId) {
    return (
      <View style={[styles.screen, { backgroundColor: theme.colors.background }]}>
        <Text style={{ color: theme.colors.text }}>Missing user.</Text>
      </View>
    );
  }

  if (!allowed || forbidden) {
    return (
      <View style={[styles.screen, { backgroundColor: theme.colors.background }]}>
        <StatusBar style={theme.isDarkMode ? 'light' : 'dark'} />
        <View style={styles.header}>
          <HamburgerMenu navigation={navigation} stopwatchRef={stopwatchRef} />
          <Text style={[styles.breadcrumb, { color: theme.colors.heading }]}>Club Stats</Text>
          <ProfileIcon navigation={navigation} user={user} />
        </View>
        <Text style={[styles.blocked, { color: theme.colors.text }]}>
          Club Stats is available for 1st degree black belt and above.
        </Text>
        <Stopwatch ref={stopwatchRef} />
      </View>
    );
  }

  return (
    <View style={[styles.screen, { backgroundColor: theme.colors.background }]}>
      <StatusBar style={theme.isDarkMode ? 'light' : 'dark'} />
      <View style={styles.header}>
        <HamburgerMenu navigation={navigation} stopwatchRef={stopwatchRef} />
        <Text style={[styles.breadcrumb, { color: theme.colors.heading }]}>Club Stats</Text>
        <ProfileIcon navigation={navigation} user={user} />
      </View>

      <ScrollView ref={scrollRef} contentContainerStyle={styles.scroll} showsVerticalScrollIndicator>
        <Text style={[styles.blurb, { color: theme.colors.text, opacity: 0.85 }]}>
          Course activity matrix: cell color reflects how many enrolled students logged at least one
          session for that course in the window. 🔴 none · 🟡 some · 🟢 all enrolled (with at least
          one eligible student).
        </Text>

        <TouchableOpacity
          style={[styles.scopeBtn, { borderColor: theme.colors.border }]}
          onPress={() => setScopeOpen(true)}
        >
          <Text style={{ color: theme.colors.heading, fontWeight: '700' }}>
            Scope:{' '}
            {scopeStudentId === 'all'
              ? 'All students'
              : students.find((s) => String(s.id) === String(scopeStudentId))?.student_name ||
                students.find((s) => String(s.id) === String(scopeStudentId))?.username ||
                `Student #${scopeStudentId}`}
          </Text>
          <Text style={{ color: theme.colors.text, fontSize: 12, marginTop: 4 }}>
            Tap to switch scope (filters entire table to one student)
          </Text>
        </TouchableOpacity>

        <View style={styles.toggleRow}>
          <Text style={{ color: theme.colors.text, marginRight: 12 }}>Metric:</Text>
          <TouchableOpacity
            style={[
              styles.toggleChip,
              metric === 'sessions' && { backgroundColor: theme.colors.heading + '33' },
            ]}
            onPress={() => setMetric('sessions')}
          >
            <Text style={{ color: theme.colors.text }}>Total sessions</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[
              styles.toggleChip,
              metric === 'avg_per_student_week' && { backgroundColor: theme.colors.heading + '33' },
            ]}
            onPress={() => setMetric('avg_per_student_week')}
          >
            <Text style={{ color: theme.colors.text }}>Avg sessions / student / week</Text>
          </TouchableOpacity>
        </View>

        <ScrollView horizontal showsHorizontalScrollIndicator>
          <View>
            <View style={[styles.tableRow, styles.tableHeader]}>
              <Text style={[styles.cellCourse, styles.th, { color: theme.colors.heading }]}>
                Course
              </Text>
              {WINDOWS.map((w) => (
                <Text key={w.key} style={[styles.cellData, styles.th, { color: theme.colors.heading }]}>
                  {w.label}
                </Text>
              ))}
            </View>
            {loading ? (
              <View
                style={[
                  styles.tableRow,
                  { borderColor: theme.isDarkMode ? 'rgba(255,255,255,0.12)' : '#ddd' },
                ]}
              >
                <Text style={[styles.emptyMatrixRow, { color: theme.colors.text }]}>
                  Loading matrix…
                </Text>
              </View>
            ) : matrix?.rows?.length ? (
              matrix.rows.map((row) => (
                <View
                  key={String(row.courseKey ?? row.courseId)}
                  style={[
                    styles.tableRow,
                    { borderColor: theme.isDarkMode ? 'rgba(255,255,255,0.12)' : '#ddd' },
                  ]}
                >
                  <Text style={[styles.cellCourse, { color: theme.colors.text }]} numberOfLines={2}>
                    {row.courseName}
                  </Text>
                  {WINDOWS.map((w) => {
                    const cell = row.cells[w.key];
                    const days = w.key === '7' ? 7 : w.key === '30' ? 30 : 90;
                    return (
                      <TouchableOpacity
                        key={w.key}
                        style={styles.cellData}
                        onPress={() => loadDetail(row.courseId, days)}
                      >
                        <Text style={{ fontSize: 16 }}>{heatEmoji(cell?.heat)}</Text>
                        <Text style={[styles.cellNum, { color: theme.colors.heading }]}>
                          {cell?.displayValue ?? '—'}
                        </Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              ))
            ) : (
              <View
                style={[
                  styles.tableRow,
                  { borderColor: theme.isDarkMode ? 'rgba(255,255,255,0.12)' : '#ddd' },
                ]}
              >
                <Text style={[styles.emptyMatrixRow, { color: theme.colors.text }]}>
                  No courses to show yet — zeros and heat colors will appear when courses and sessions
                  exist.
                </Text>
              </View>
            )}
          </View>
        </ScrollView>

        <View
          onLayout={(e) => {
            detailYRef.current = e.nativeEvent.layout.y;
          }}
          style={styles.detailSection}
        >
          <Text style={[styles.detailTitle, { color: theme.colors.heading }]}>
            Session detail
          </Text>
          {selected ? (
            <Text style={[styles.detailSub, { color: theme.colors.text }]}>
              Course #{selected.courseId} · last {selected.days} days · tap a cell above to refresh
            </Text>
          ) : (
            <Text style={[styles.detailSub, { color: theme.colors.text }]}>
              Tap a figure in the matrix to list students (enrolled in that course) and their session
              counts.
            </Text>
          )}
          {detailLoading ? (
            <ActivityIndicator color={theme.colors.heading} style={{ marginVertical: 16 }} />
          ) : detail ? (
            <View style={[styles.detailTable, { borderColor: theme.colors.border }]}>
              <View style={styles.detailHeadRow}>
                <Text style={[styles.dColName, { color: theme.colors.heading }]}>Student</Text>
                <Text style={[styles.dColNum, { color: theme.colors.heading }]}>Sessions</Text>
              </View>
              {(detail.students || []).map((s) => (
                <View key={s.id} style={styles.detailRow}>
                  <Text style={[styles.dColName, { color: theme.colors.text }]}>{s.name}</Text>
                  <Text style={[styles.dColNum, { color: theme.colors.text }]}>{s.sessionCount}</Text>
                </View>
              ))}
            </View>
          ) : null}
        </View>
      </ScrollView>

      <Modal visible={scopeOpen} transparent animationType="fade">
        <Pressable style={styles.modalBackdrop} onPress={() => setScopeOpen(false)}>
          <Pressable style={[styles.modalSheet, { backgroundColor: theme.colors.surface }]}>
            <Text style={[styles.modalTitle, { color: theme.colors.heading }]}>Scope</Text>
            <ScrollView style={{ maxHeight: 400 }}>
              <TouchableOpacity
                style={styles.modalRow}
                onPress={() => {
                  setScopeStudentId('all');
                  setScopeOpen(false);
                }}
              >
                <Text style={{ color: theme.colors.text, fontWeight: '700' }}>All students</Text>
              </TouchableOpacity>
              {students.map((s) => (
                <TouchableOpacity
                  key={s.id}
                  style={styles.modalRow}
                  onPress={() => {
                    setScopeStudentId(String(s.id));
                    setScopeOpen(false);
                  }}
                >
                  <Text style={{ color: theme.colors.text }}>
                    {s.student_name || s.username} (#{s.id})
                  </Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </Pressable>
        </Pressable>
      </Modal>

      <Stopwatch ref={stopwatchRef} />
    </View>
  );
}

function getStyles(theme) {
  return StyleSheet.create({
    screen: { flex: 1 },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: 16,
      paddingTop: Platform.OS === 'web' ? 16 : 48,
      paddingBottom: 12,
    },
    breadcrumb: { fontSize: 18, fontWeight: '700', flex: 1, textAlign: 'center' },
    scroll: { padding: 16, paddingBottom: 48 },
    blurb: { marginBottom: 14, fontSize: 13, lineHeight: 19 },
    scopeBtn: {
      borderWidth: 1,
      borderRadius: 10,
      padding: 12,
      marginBottom: 14,
    },
    toggleRow: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 },
    toggleChip: {
      borderWidth: 1,
      borderColor: theme.colors.border,
      paddingVertical: 8,
      paddingHorizontal: 10,
      borderRadius: 8,
      marginRight: 8,
      marginBottom: 8,
    },
    tableRow: {
      flexDirection: 'row',
      alignItems: 'stretch',
      borderBottomWidth: StyleSheet.hairlineWidth,
      minWidth: 520,
    },
    tableHeader: { borderBottomWidth: 2 },
    th: { fontWeight: '700', fontSize: 12 },
    cellCourse: { width: 160, paddingVertical: 10, paddingHorizontal: 6, fontSize: 13 },
    emptyMatrixRow: {
      minWidth: 520,
      paddingVertical: 14,
      paddingHorizontal: 10,
      fontSize: 13,
      lineHeight: 18,
    },
    cellData: {
      width: 110,
      paddingVertical: 10,
      alignItems: 'center',
      justifyContent: 'center',
    },
    cellNum: { fontSize: 14, fontWeight: '600', marginTop: 2 },
    detailSection: { marginTop: 28 },
    detailTitle: { fontSize: 17, fontWeight: '800', marginBottom: 6 },
    detailSub: { fontSize: 13, marginBottom: 10 },
    detailTable: { borderWidth: 1, borderRadius: 8, overflow: 'hidden' },
    detailHeadRow: { flexDirection: 'row', backgroundColor: theme.isDarkMode ? '#2a2a2a' : '#eee' },
    detailRow: {
      flexDirection: 'row',
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: theme.colors.border,
    },
    dColName: { flex: 1, padding: 10, fontSize: 14 },
    dColNum: { width: 88, padding: 10, fontSize: 14, textAlign: 'right' },
    blocked: { padding: 24, fontSize: 15, textAlign: 'center' },
    modalBackdrop: {
      flex: 1,
      backgroundColor: 'rgba(0,0,0,0.45)',
      justifyContent: 'center',
      padding: 24,
    },
    modalSheet: { borderRadius: 12, padding: 16, maxHeight: '85%' },
    modalTitle: { fontSize: 17, fontWeight: '700', marginBottom: 10 },
    modalRow: {
      paddingVertical: 12,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: '#8883',
    },
  });
}
